import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './VendorAgentTab.css';

const VendorAgentTab = () => {
  const { authFetch } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [agents, setAgents] = useState([]);
  const [newVendor, setNewVendor] = useState('');
  const [newAgent, setNewAgent] = useState({ vendor_id: '', name: '' });
  const [loading, setLoading] = useState(true);

  const [editModalConfig, setEditModalConfig] = useState({ isOpen: false, type: '', data: null });
  const [editModalName, setEditModalName] = useState('');

  const fetchVendors = async () => {
    try {
      const res = await authFetch('/api/vendors');
      const data = await res.json();
      if (data.status === 'success') {
        setVendors(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await authFetch('/api/agents');
      const data = await res.json();
      if (data.status === 'success') {
        setAgents(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchVendors(), fetchAgents()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFetch]);

  const handleAddVendor = async (e) => {
    e.preventDefault();
    if (!newVendor) return;
    
    try {
      const res = await authFetch('/api/vendors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newVendor }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        toast.success(`Vendor ${newVendor} added.`);
        setNewVendor('');
        fetchVendors();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error('Failed to add vendor.');
    }
  };

  const handleAddAgent = async (e) => {
    e.preventDefault();
    if (!newAgent.vendor_id || !newAgent.name) return;

    try {
      const res = await authFetch('/api/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAgent),
      });
      const data = await res.json();
      if (data.status === 'success') {
        toast.success(`Agent ${newAgent.name} added.`);
        setNewAgent({ vendor_id: '', name: '' });
        fetchAgents();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error('Failed to add agent.');
    }
  };

  const toggleVendor = async (id, currentStatus) => {
    try {
      const res = await authFetch(`/api/vendors/${id}/toggle`, {
        method: 'PUT',
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchVendors();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const toggleAgent = async (id, currentStatus) => {
    try {
      const res = await authFetch(`/api/agents/${id}/toggle`, {
        method: 'PUT',
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchAgents();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const openEditModal = (type, data) => {
    setEditModalConfig({ isOpen: true, type, data });
    setEditModalName(data.name);
  };

  const handleSaveModal = async (e) => {
    e.preventDefault();
    const { type, data } = editModalConfig;
    const url = type === 'vendor' ? `/api/vendors/${data.id}` : `/api/agents/${data.id}`;
    
    try {
      const res = await authFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editModalName })
      });
      const resData = await res.json();
      if (resData.status === 'success') {
        toast.success(`${type} updated to ${editModalName}`);
        if(type === 'vendor') fetchVendors(); else fetchAgents();
        setEditModalConfig({ isOpen: false, type: '', data: null });
      } else {
        toast.error(resData.message);
      }
    } catch (err) {
      toast.error(`Failed to edit ${type}`);
    }
  };

  const handleDeleteVendor = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete vendor "${name}"?`)) return;
    try {
      const res = await authFetch(`/api/vendors/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        toast.success(data.message);
        fetchVendors();
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (err) {
      toast.error('Failed to delete vendor');
    }
  };

  const handleDeleteAgent = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete agent "${name}"?`)) return;
    try {
      const res = await authFetch(`/api/agents/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        toast.success(data.message);
        fetchAgents();
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (err) {
      toast.error('Failed to delete agent');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="va-container">
      <div className="va-section">
        <h3>Vendor Management</h3>
        <form onSubmit={handleAddVendor} className="va-form">
          <input 
            type="text" 
            placeholder="New Vendor Name" 
            value={newVendor} 
            onChange={(e) => setNewVendor(e.target.value)} 
          />
          <button type="submit">Add Vendor</button>
        </form>
        <table className="va-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Vendor Name</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map(v => (
              <tr key={v.id}>
                <td>{v.id}</td>
                <td>{v.name}</td>
                <td>
                  <label className="switch-table">
                    <input 
                      type="checkbox" 
                      checked={v.is_active} 
                      onChange={() => toggleVendor(v.id, v.is_active)}
                    />
                    <span className="slider round"></span>
                  </label>
                </td>
                <td className="action-buttons">
                  <button className="btn-edit" onClick={() => openEditModal('vendor', v)} title="Edit">Edit</button>
                  <button className="btn-delete" onClick={() => handleDeleteVendor(v.id, v.name)} title="Delete">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="va-section">
        <h3>Agent Management</h3>
        <form onSubmit={handleAddAgent} className="va-form">
          <select 
            value={newAgent.vendor_id} 
            onChange={(e) => setNewAgent({ ...newAgent, vendor_id: e.target.value })}
          >
            <option value="">-- Select Vendor --</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <input 
            type="text" 
            placeholder="New Agent Name (e.g. B2B)" 
            value={newAgent.name} 
            onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })} 
          />
          <button type="submit">Add Agent</button>
        </form>
        <table className="va-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Agent Name</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {agents.map(a => (
              <tr key={a.id}>
                <td>{a.vendor_name}</td>
                <td>{a.name}</td>
                <td>
                  <label className="switch-table">
                    <input 
                      type="checkbox" 
                      checked={a.is_active} 
                      onChange={() => toggleAgent(a.id, a.is_active)}
                    />
                    <span className="slider round"></span>
                  </label>
                </td>
                <td className="action-buttons">
                  <button className="btn-edit" onClick={() => openEditModal('agent', a)} title="Edit">Edit</button>
                  <button className="btn-delete" onClick={() => handleDeleteAgent(a.id, a.name)} title="Delete">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editModalConfig.isOpen && (
        <div className="modal-overlay" onClick={() => setEditModalConfig({ isOpen: false, type: '', data: null })}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Edit {editModalConfig.type === 'vendor' ? 'Vendor' : 'Agent'}</h2>
            <form onSubmit={handleSaveModal}>
              <div className="va-form" style={{ display: 'block' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Name</label>
                <input 
                  type="text" 
                  value={editModalName} 
                  onChange={e => setEditModalName(e.target.value)} 
                  required 
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setEditModalConfig({ isOpen: false, type: '', data: null })}>Cancel</button>
                <button type="submit" className="btn-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorAgentTab;
